import React, { useState, useEffect } from "react";
import Comments from '../components/Comments';
import useAdminVerification from '../hooks/useAdminVerification';
import { useToast } from '../components/Toast';

export default function UpdateLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '기능 업데이트',
    is_visible: true
  });
  
  const { isAdminVerified } = useAdminVerification();
  const toast = useToast();

  // 다크모드 상태 업데이트
  useEffect(() => {
    const checkDarkMode = () => {
      const isDarkClass = document.body.classList.contains('dark-mode');
      const isDarkMedia = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(isDarkClass || isDarkMedia);
    };
    
    // 초기 확인
    checkDarkMode();
    
    // 미디어 쿼리 변화 감지
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkDarkMode);
    
    // MutationObserver로 body 클래스 변화 감지
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => {
      mediaQuery.removeEventListener('change', checkDarkMode);
      observer.disconnect();
    };
  }, []);

  // 업데이트 로그 데이터 가져오기
  const fetchUpdateLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      };
      
      // 토큰이 있으면 Authorization 헤더 추가
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api/updatelog`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        // 문자열 "\n"을 실제 줄바꿈 문자로 변환
        const processedData = data.map(log => ({
          ...log,
          content: log.content.replace(/\\n/g, '\n')
        }));
        setLogs(processedData);
      } else {
        throw new Error('업데이트 로그를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('업데이트 로그 로딩 오류:', error);
      setError(error.message);
      // 에러 발생시 기본 데이터 사용
      setLogs([
        { created_at: "2025-08-15", title: "사이트 리뉴얼", content: "데이터 사이트 2.0 오픈\n전체 UI개선 작업", category: "기능 업데이트" },
        { created_at: "2025-01-01", title: "사이트 오픈", content: "데이터 사이트 운영 시작", category: "사이트 오픈" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdateLogs();
  }, []);

  // 폼 데이터 변경 처리
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 새 업데이트 로그 생성
  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('제목과 내용을 모두 입력해주세요.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/updatelog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('업데이트 로그가 생성되었습니다.');
        setFormData({ title: '', content: '', category: '기능 업데이트', is_visible: true });
        setShowAdminPanel(false);
        fetchUpdateLogs();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || '생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('생성 오류:', error);
      toast.error('생성 중 오류가 발생했습니다.');
    }
  };

  // 업데이트 로그 수정
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('제목과 내용을 모두 입력해주세요.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/updatelog/${editingLog.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('업데이트 로그가 수정되었습니다.');
        setEditingLog(null);
        setFormData({ title: '', content: '', category: '기능 업데이트', is_visible: true });
        setShowAdminPanel(false);
        fetchUpdateLogs();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || '수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('수정 오류:', error);
      toast.error('수정 중 오류가 발생했습니다.');
    }
  };

  // 업데이트 로그 삭제
  const handleDelete = async (logId, title) => {
    setDeleteTarget({ id: logId, title });
    setShowDeleteModal(true);
  };

  // 실제 삭제 실행
  const executeDelete = async (permanent = false) => {
    if (!deleteTarget) return;

    try {
      const token = localStorage.getItem('token');
      const deleteUrl = permanent 
        ? `/api/updatelog/${deleteTarget.id}?permanent=true`
        : `/api/updatelog/${deleteTarget.id}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const message = permanent 
          ? '업데이트 로그가 영구적으로 삭제되었습니다.' 
          : '업데이트 로그가 숨김 처리되었습니다.';
        toast.success(message);
        
        // 즉시 상태에서 삭제된 로그 제거
        setLogs(prevLogs => prevLogs.filter(log => log.id !== deleteTarget.id));
        // 서버에서 최신 데이터 다시 가져오기
        await fetchUpdateLogs();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || '삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('삭제 오류:', error);
      toast.error('삭제 중 오류가 발생했습니다.');
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  // 수정 모드 시작
  const startEdit = (log) => {
    setEditingLog(log);
    setFormData({
      title: log.title,
      content: log.content,
      category: log.category,
      is_visible: log.is_visible !== undefined ? log.is_visible : true
    });
    setShowAdminPanel(true);
  };

  // 수정 취소
  const cancelEdit = () => {
    setEditingLog(null);
    setFormData({ title: '', content: '', category: '기능 업데이트', is_visible: true });
    setShowAdminPanel(false);
  };

  // 카테고리별 색상 설정
  const getCategoryColor = (category) => {
    switch (category) {
      case '데이터 갱신': return '#4CAF50';
      case '기능 업데이트': return '#2196F3';
      case '업데이트 예정': return '#FF9800';
      case '버그 수정': return '#F44336';
      case '사이트 오픈': return '#9C27B0';
      default: return '#1976d2';
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="updatelog-container" style={{ textAlign: "center" }}>
        <h1 className="updatelog-title">업데이트 기록</h1>
        <div>업데이트 로그를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <>
      <div className="updatelog-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="updatelog-title">업데이트 기록</h1>
          {isAdminVerified && (
            <button 
              className="admin-toggle-btn"
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              style={{
                margin: '0px',
                padding: '8px 16px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showAdminPanel ? '패널 닫기' : '로그 작성하기'}
            </button>
          )}
        </div>

        {/* 관리자 패널 */}
        {isAdminVerified && showAdminPanel && (
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h3>{editingLog ? '업데이트 로그 수정' : '새 업데이트 로그 작성'}</h3>
            <form onSubmit={editingLog ? handleUpdate : handleCreate}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>제목:</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)'
                  }}
                  placeholder="업데이트 제목을 입력하세요"
                />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>카테고리:</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="기능 업데이트">기능 업데이트</option>
                  <option value="데이터 갱신">데이터 갱신</option>
                  <option value="버그 수정">버그 수정</option>
                  <option value="업데이트 예정">업데이트 예정</option>
                  <option value="사이트 오픈">사이트 오픈</option>
                </select>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>표시 여부:</label>
                <div 
                  onClick={() => setFormData(prev => ({ ...prev, is_visible: !prev.is_visible }))}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: '50px',
                    height: '24px',
                    backgroundColor: formData.is_visible ? '#4CAF50' : '#ccc',
                    borderRadius: '12px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'white',
                      borderRadius: '10px',
                      position: 'absolute',
                      left: formData.is_visible ? '26px' : '2px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
                <span style={{ marginLeft: '10px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {formData.is_visible ? '사용자에게 보임' : '숨김'}
                </span>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>내용:</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  rows="6"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    resize: 'vertical'
                  }}
                  placeholder="업데이트 내용을 입력하세요 (각 항목은 새 줄로 구분)"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: editingLog ? '#4CAF50' : '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {editingLog ? '수정하기' : '작성하기'}
                </button>
                
                {editingLog && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#757575',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    취소
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {error && (
          <div className="updatelog-error">
            ⚠️ {error} (기본 데이터를 표시합니다)
          </div>
        )}
        
        <ul className="updatelog-list">
          {logs.map((log, idx) => (
            <li 
              key={log.id || idx} 
              className={`updatelog-item ${isAdminVerified && (!log.is_visible || log.is_visible === 0) ? 'hidden-log' : ''}`}
              style={{
                ...(isAdminVerified && (!log.is_visible || log.is_visible === 0) ? {
                  backgroundColor: isDark 
                    ? 'rgba(255, 87, 34, 0.25)' 
                    : 'rgba(255, 87, 34, 0.1)',
                  boxShadow: '0 0 8px rgba(255, 87, 34, 0.3)',
                  position: 'relative'
                } : {})
              }}
            >
              <div className="updatelog-header" style={{ color: getCategoryColor(log.category) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isAdminVerified && (!log.is_visible || log.is_visible === 0 || log.is_visible === false) && (
                    <span style={{
                      backgroundColor: isDark ? '#4a1e1e' : '#a18224ff',
                      color: isDark ? '#ff6b6b' : '#ffe9a2ff',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      숨김
                    </span>
                  )}
                  <span>{formatDate(log.created_at)} - {log.title}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {log.category && (
                    <span className="updatelog-category" style={{ backgroundColor: getCategoryColor(log.category) }}>
                      {log.category}
                    </span>
                  )}
                  {isAdminVerified && log.id && (
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => startEdit(log)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '12px',
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(log.id, log.title)}
                        style={{
                          padding: '2px 6px',
                          fontSize: '12px',
                          backgroundColor: '#F44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="updatelog-content">
                {log.content.split('\n').map((line, i) => {
                  const isDashLine = line.trim().startsWith('-') || line.trim().startsWith(' -');
                  const displayText = isDashLine ? line.replace(/^\s*-\s*/, '') : line;
                  return (
                    <div 
                      key={i} 
                      className="updatelog-content-item"
                      style={isDashLine ? { 
                        marginLeft: '20px',
                        paddingLeft: '5px',
                        borderLeft: `3px solid ${getCategoryColor(log.category)}`,
                        borderRadius: '0 4px 4px 0'
                      } : {}}
                    >
                      {!isDashLine && (
                        <span className="updatelog-bullet">●</span>
                      )}
                      <span className="updatelog-text">{displayText}</span>
                    </div>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
        {logs.length === 0 && !loading && (
          <div style={{ textAlign: "center", color: "var(--text-muted)", padding: 40 }}>
            📝 업데이트 로그가 없습니다.
          </div>
        )}
      </div>
      
      <Comments pageName="updatelog" />

      {/* 삭제 확인 모달 */}
      {showDeleteModal && deleteTarget && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <h3>업데이트 로그 삭제</h3>
            
            <p>
              "<strong>{deleteTarget.title}</strong>" 업데이트 로그를 삭제하시겠습니까?
            </p>

            <div className="delete-modal-buttons">
              <button
                className="delete-modal-btn delete"
                onClick={() => executeDelete(true)}
              >
                영구 삭제
              </button>
              
              <button
                className="delete-modal-btn hide"
                onClick={() => executeDelete(false)}
              >
                숨김 처리
              </button>
              
              <button
                className="delete-modal-btn cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTarget(null);
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
